import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import { AlertCircle, Loader2, Trash2 } from 'lucide-react'

interface RemoveTarget {
  id: number
  name: string
  clientIdMasked: string
}

interface RemoveConnectionDialogProps {
  /**
   * `/a/:account_slug/settings` — the business's settings root, served by the backend
   * (`currentAccount.settingsPath`). Every endpoint below is this plus a FIXED suffix.
   */
  settingsPath: string
  connection: RemoveTarget | null
  onClose: () => void
}

// Tidying away an EMPTIED connection.
//
// A swap deliberately keeps the old connection row ("kept, just emptied"), and until now
// nothing in the operator app could delete one — so a rescued operator was left staring at a
// permanent tombstone card with no properties and no way to make it go away. The button that
// opens this dialog is offered ONLY when the connection serves zero properties GLOBALLY and
// this business owns it; the backend enforces both again.
export default function RemoveConnectionDialog({ settingsPath, connection, onClose }: RemoveConnectionDialogProps) {
  const { addToast } = useToast()
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    setError(null)
    setRemoving(false)
  }, [connection?.id])

  async function remove() {
    if (!connection) return
    setError(null)
    setRemoving(true)

    try {
      const response = await apiFetch(`${settingsPath}/pms-connections/${connection.id}`, { method: 'DELETE' })
      const data = await response.json()

      if (!response.ok) {
        setError(data.errors?.join(', ') || 'We could not remove this connection.')
        return
      }

      addToast({
        variant: 'success',
        title: 'Connection removed',
        description: `${data.name ?? connection.name} is no longer listed.`,
      })
      onClose()
      router.reload({ only: ['connections'] })
    } catch (e) {
      setError(apiErrorMessage(e, 'Network error. Please check your connection and try again.'))
    } finally {
      setRemoving(false)
    }
  }

  return (
    <AlertDialog open={connection !== null} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove {connection?.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            This connection isn&apos;t serving any properties, so removing it changes nothing about your pricing. You
            can always connect the same PMS account again later.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogBody className="space-y-4">
          <div className="rounded-lg border border-border/60 bg-muted/40 px-3 py-2.5">
            <p className="text-xs text-muted-foreground">
              Client ID <span className="ml-1 font-mono tabular-nums text-foreground">{connection?.clientIdMasked}</span>
            </p>
          </div>

          {error && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </AlertDialogBody>

        <AlertDialogFooter>
          <Button type="button" variant="ghost" className="rounded-lg" onClick={onClose} disabled={removing}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="rounded-lg"
            onClick={remove}
            disabled={removing}
            aria-busy={removing}
          >
            {removing ? (
              <>
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                Removing…
              </>
            ) : (
              <>
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Remove connection
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
